// 取り込みテンプレート (外国人情報_インポート用.xlsx) の生成。
// 企業インポート (features/company/utiles/excel.ts) と同じ方針: ヘッダ + サンプル行 + 非表示シートの選択肢 + 入力規則。
// 選択肢はログインユーザのマスタ (所属機関 / 国籍 / 受験分野 / 日本語能力 / 性別 / ステータス) から動的に埋めるため、
// 配布ファイルを更新しなくても常に最新のマスタでドロップダウンが張られる。
// 居住地 (都道府県 / 市区町村) は自由入力 (取り込み後の一括登録画面で選択・補正する)。

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  getLearnerImportColumns,
  LEARNER_IMPORT_MASTER_SHEET_NAME,
  LEARNER_IMPORT_SHEET_NAME,
  LEARNER_IMPORT_TEMPLATE_ROWS,
  TLearnerImportColumnFormat,
} from "@/features/learner/components/learner-file-import/learner-import-columns";

export const LEARNER_IMPORT_TEMPLATE_FILE_NAME = "外国人情報_インポート用.xlsx";

// 配布テンプレート (Excel で保存されたもの) と同じ書式にするための見た目の定義。
// exceljs の既定フォントは Calibri (欧文) のため、日本語 Excel の既定と同じ 游ゴシック を明示する。
const TEMPLATE_FONT = { name: "游ゴシック", family: 3, charset: 128 } as const;
// 日本語 Excel (游ゴシック 11pt) の既定行高。exceljs の既定 15 のままだと行が詰まって見える。
const TEMPLATE_DEFAULT_ROW_HEIGHT = 18.75;
// 日付セルの表示書式。文字列 "2024/1/1" ではなく日付セルとして入力されていることが分かるようにする。
const TEMPLATE_DATE_FORMAT = "yyyy/mm/dd";
// Excel の「文字列」書式。管理番号が数値化されるのを防ぐ (先頭 0 落ち・15桁超の指数表記)。
const TEMPLATE_TEXT_FORMAT = "@";
// 列の format → 入力欄セルに張る表示書式。number は記入例セルを数値にするだけで書式は張らない (Excel の「標準」)。
const CELL_NUM_FMT: Partial<Record<TLearnerImportColumnFormat, string>> = {
  date: TEMPLATE_DATE_FORMAT,
  text: TEMPLATE_TEXT_FORMAT,
};

// サンプル行のセル値。format 指定に応じて日付・数値セルにする (文字列のままだと左寄せの文字列セルになる)。
// 日付は UTC の 0時 で作る (ローカル時刻で作るとシリアル値が前日にずれ、取り込み時に1日前の日付になる)。
const toSampleCellValue = (sample: string, format?: TLearnerImportColumnFormat): ExcelJS.CellValue => {
  if (format === "number") {
    const numeric = Number(sample);
    return Number.isFinite(numeric) ? numeric : sample;
  }
  if (format === "date") {
    const matched = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(sample);
    if (matched) return new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3])));
  }
  return sample;
};

// exceljs の型定義には範囲指定の入力規則 API (worksheet.dataValidations.add) が無いため、ここだけ型を補う。
type TWorksheetWithDataValidations = ExcelJS.Worksheet & {
  dataValidations: { add: (range: string, validation: ExcelJS.DataValidation) => void };
};

// テンプレートのドロップダウンに載せる選択肢。
export type TLearnerImportTemplateOptions = {
  // 企業アカウントは所属機関を持たないため、所属機関列そのものをテンプレートから外す。
  isCompanyUser: boolean;
  companyNames: string[];
  nationalityNames: string[];
  industryNames: string[];
  japaneseLevelNames: string[];
  genderNames: string[];
  statusNames: string[];
};

// Master シートの列と、対応する入力シートの列キー。
// 性別 / ステータス / 日本語能力 も enum マスタ (EGender / EPlanStatus / EJapaneseLevelType) の取得値を使い、
// 画面のドロップダウンとテンプレートの選択肢が常に一致するようにする。
const MASTER_COLUMNS: { column: string; targetKey: string; values: (opts: TLearnerImportTemplateOptions) => string[] }[] = [
  { column: "A", targetKey: "companyName", values: (o) => o.companyNames },
  { column: "B", targetKey: "nationality", values: (o) => o.nationalityNames },
  { column: "C", targetKey: "industryName", values: (o) => o.industryNames },
  { column: "D", targetKey: "japaneseLevel", values: (o) => o.japaneseLevelNames },
  { column: "E", targetKey: "gender", values: (o) => o.genderNames },
  { column: "F", targetKey: "status", values: (o) => o.statusNames },
];

export const downloadLearnerImportTemplate = async (options: TLearnerImportTemplateOptions) => {
  // 入力シートの列構成 (企業アカウントは所属機関列なし)。並び順がそのまま列位置になる。
  const columns = getLearnerImportColumns(options.isCompanyUser);
  // 列キー → Excel の列文字 (A, B, ...)。存在しない列 (企業アカウントの所属機関) は null。
  const columnLetter = (key: string): string | null => {
    const index = columns.findIndex((col) => col.key === key);
    return index < 0 ? null : String.fromCharCode("A".charCodeAt(0) + index);
  };

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(LEARNER_IMPORT_SHEET_NAME, { properties: { defaultRowHeight: TEMPLATE_DEFAULT_ROW_HEIGHT } });
  // ヘッダ行を固定 (ウィンドウ枠の固定)。下へスクロールしても列名が見えるようにする。
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  const masterSheet = workbook.addWorksheet(LEARNER_IMPORT_MASTER_SHEET_NAME);

  // 選択肢は非表示シートへ。veryHidden で Excel の「シートの再表示」からも出さない。
  MASTER_COLUMNS.forEach(({ column, values }) => {
    values(options).forEach((value, idx) => {
      masterSheet.getCell(`${column}${idx + 1}`).value = value;
    });
  });
  masterSheet.state = "veryHidden";

  // 列幅とフォントは行を追加する前に設定する。exceljs の列スタイルは設定時点の既存セルにも上書きされるため、
  // 行の追加後に設定するとヘッダの塗り・罫線が消える。
  // 列フォントを張るのは、exceljs の既定フォントが Calibri で、利用者が入力する行だけ書体が変わってしまうため
  // (配布テンプレートはブック既定が 游ゴシック なので、シート全体が同じ書体になっている)。
  columns.forEach((col, index) => {
    const column = sheet.getColumn(index + 1);
    column.width = col.width;
    column.font = { ...TEMPLATE_FONT, size: 11 };
  });

  const headerRow = sheet.addRow(columns.map((col) => col.header));
  // サンプル行: マスタ由来の列は実マスタの先頭値を入れる。固定文字列 (外食業 等) だとログインユーザのマスタに
  // 存在せず、サンプル行をそのまま取り込んだときに「未突合」になって分野が空のまま画面に出てしまう。
  const masterSampleByKey = new Map(MASTER_COLUMNS.map(({ targetKey, values }) => [targetKey, values(options)[0]]));
  const sampleRow = sheet.addRow(
    columns.map((col) => {
      const masterSample = masterSampleByKey.get(col.key);
      // マスタ由来の列は常に文字列 (ドロップダウンの選択肢と一致させる)。
      return masterSample || toSampleCellValue(col.sample, col.format);
    }),
  );
  // 記入例は本文と同じ通常書式にする (配布テンプレートに合わせる)。そのまま残して取り込めば、その内容で登録される。
  sampleRow.eachCell((cell) => {
    cell.font = { ...TEMPLATE_FONT, size: 11 };
  });

  headerRow.eachCell((cell) => {
    cell.font = { ...TEMPLATE_FONT, bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
  });

  // 入力欄の最終行 (記入例の2行目から LEARNER_IMPORT_TEMPLATE_ROWS 行ぶん)。表示書式と入力規則で共有する。
  const lastRow = LEARNER_IMPORT_TEMPLATE_ROWS + 1;

  // 表示書式は入力規則と同じ 2行目〜lastRow 行に張る。記入例セルだけだと、利用者が入力した値が
  // 日付は Excel の地域設定の既定書式 (dd/mm/yyyy 等) になり (yyyy/mm/dd と入力しても表示が変わる)、
  // 管理番号は数値化されてしまう (先頭 0 落ち・15桁超の指数表記)。
  // 列スタイルではなくセルに張るのは、行の追加前にセルを作ると addRow がその後ろに追加され、
  // ヘッダ行の位置がずれてしまうため。列に設定したフォントはセル側に引き継がれる。
  columns.forEach((col, index) => {
    const numFmt = col.format && CELL_NUM_FMT[col.format];
    if (!numFmt) return;
    for (let row = 2; row <= lastRow; row++) sheet.getCell(row, index + 1).numFmt = numFmt;
  });

  // 入力規則 (ドロップダウン)。2行目以降 LEARNER_IMPORT_TEMPLATE_ROWS 行ぶんに張る。
  // 列ごとに範囲 (例: M2:M201) で1件だけ登録する。セル単位で設定すると exceljs が
  // 重複・重なった sqref (M2:M201 と M10:M201) を書き出し、Excel が「修復」で入力規則を全削除してしまう。
  // formula1 は `=` を付けない (`=Master!$D$1:$D$5` は不正な数式として無視される)。
  const applyListValidation = (targetKey: string, formula: string) => {
    const letter = columnLetter(targetKey);
    // 入力シートに無い列 (企業アカウントの所属機関) には張らない。
    if (!letter) return;
    (sheet as TWorksheetWithDataValidations).dataValidations.add(`${letter}2:${letter}${lastRow}`, {
      type: "list",
      allowBlank: true,
      formulae: [formula],
    });
  };

  MASTER_COLUMNS.forEach(({ column, targetKey, values }) => {
    const count = values(options).length;
    // 選択肢が無いマスタは入力規則を張らない (空範囲の参照は Excel で不正になる)。
    if (count === 0) return;
    applyListValidation(targetKey, `${LEARNER_IMPORT_MASTER_SHEET_NAME}!$${column}$1:$${column}$${count}`);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), LEARNER_IMPORT_TEMPLATE_FILE_NAME);
};
